import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import "dayjs/locale/ar-sa";
import { Clear } from "@mui/icons-material";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import { Box, IconButton, Popover, Tooltip } from "@mui/material";
import { CalendarPicker } from "@mui/x-date-pickers/CalendarPicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { UITextField } from "../../ui";
import { clx } from "../../../utils/Helpers";
import "./DateRangeFilterInput.scss";

const DATE_VALUE_FORMAT = "YYYY-MM-DD";
const DATE_DISPLAY_FORMAT = "DD/MM/YYYY";
const MONTH_LABEL_FORMAT = "MMM YYYY";

const toDateValue = (date) =>
  date && dayjs(date).isValid() ? dayjs(date).format(DATE_VALUE_FORMAT) : "";

const isSameDate = (firstDate, secondDate) =>
  Boolean(firstDate && secondDate && dayjs(firstDate).isSame(secondDate, "day"));

const isDateBetween = (date, startDate, endDate) =>
  Boolean(
    date &&
      startDate &&
      endDate &&
      dayjs(date).isAfter(startDate, "day") &&
      dayjs(date).isBefore(endDate, "day"),
  );

const getInitialVisibleMonth = (startDate, endDate) => {
  const anchor = startDate || endDate;
  if (anchor && dayjs(anchor).isValid()) {
    return dayjs(anchor).startOf("month");
  }
  return dayjs().startOf("month");
};

const DateRangeFilterInput = ({
  name,
  placeholder,
  value,
  onChange,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const adapterLocale = lang === "ar" ? "ar-sa" : "en";
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const startDate = value?.startDate || "";
  const endDate = value?.endDate || "";
  const [visibleMonth, setVisibleMonth] = useState(() =>
    getInitialVisibleMonth(startDate, endDate),
  );

  useEffect(() => {
    if (open) {
      setVisibleMonth(getInitialVisibleMonth(startDate, endDate));
    }
  }, [endDate, open, startDate]);

  const leftMonth = visibleMonth;
  const rightMonth = visibleMonth.add(1, "month");

  const rangePlaceholder = placeholder;

  const displayValue = useMemo(() => {
    const formattedStart = startDate
      ? dayjs(startDate).format(DATE_DISPLAY_FORMAT)
      : "";
    const formattedEnd = endDate ? dayjs(endDate).format(DATE_DISPLAY_FORMAT) : "";

    if (formattedStart && formattedEnd) {
      return `${formattedStart} - ${formattedEnd}`;
    }

    return formattedStart || formattedEnd;
  }, [endDate, startDate]);

  const updateRange = (nextRange) => {
    const normalizedRange = {
      startDate: nextRange.startDate || "",
      endDate: nextRange.endDate || "",
    };
    onChange(
      normalizedRange.startDate || normalizedRange.endDate
        ? normalizedRange
        : null,
    );
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange(null);
  };

  const handleCalendarChange = (date) => {
    const selectedDate = toDateValue(date);
    if (!selectedDate) return;

    if (!startDate || endDate) {
      updateRange({ startDate: selectedDate, endDate: "" });
      return;
    }

    if (dayjs(selectedDate).isBefore(startDate, "day")) {
      updateRange({ startDate: selectedDate, endDate: startDate });
    } else {
      updateRange({ startDate, endDate: selectedDate });
    }
    setAnchorEl(null);
  };

  const renderRangeDay = (day, _selectedDays, pickersDayProps) => {
    const dayValue = toDateValue(day);
    const isStart = isSameDate(dayValue, startDate);
    const isEnd = isSameDate(dayValue, endDate);
    const isInRange = isDateBetween(dayValue, startDate, endDate);
    const { outsideCurrentMonth, ...dayProps } = pickersDayProps;

    return (
      <Box
        className={clx(
          "date-range-filter__day-wrap",
          isInRange && "date-range-filter__day-wrap--in-range",
          // The half-cell range strip only once both ends are picked — a lone
          // start date is just the circle.
          isStart && endDate && "date-range-filter__day-wrap--start",
          isEnd && startDate && "date-range-filter__day-wrap--end",
          outsideCurrentMonth && "date-range-filter__day-wrap--outside",
        )}
      >
        <PickersDay
          {...dayProps}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          className={clx(
            dayProps.className,
            "date-range-filter__day",
            isInRange && "date-range-filter__day--in-range",
            isStart && "date-range-filter__day--start",
            isEnd && "date-range-filter__day--end",
          )}
          selected={isStart || isEnd}
        />
      </Box>
    );
  };

  const leftMonthLabel = leftMonth
    .locale(adapterLocale)
    .format(MONTH_LABEL_FORMAT);
  const rightMonthLabel = rightMonth
    .locale(adapterLocale)
    .format(MONTH_LABEL_FORMAT);

  const renderMonthCalendar = (month) => (
    <CalendarPicker
      key={month.format("YYYY-MM")}
      date={null}
      defaultCalendarMonth={month.locale(adapterLocale)}
      views={["day"]}
      onChange={handleCalendarChange}
      renderDay={renderRangeDay}
      showDaysOutsideCurrentMonth
      className="date-range-filter__calendar"
    />
  );

  return (
    <>
      <Tooltip
        title={displayValue || ""}
        disableHoverListener={!displayValue || open}
        disableFocusListener={!displayValue || open}
        placement="bottom-start"
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "var(--text-body-sm-font-size, 0.875rem)",
              lineHeight: "var(--text-body-sm-line-height, 1.25rem)",
              maxWidth: 360,
              padding: "var(--space-8, 8px) var(--space-12, 12px)",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          },
        }}
      >
        <Box className="date-range-filter">
          <UITextField
            name={name}
            value={displayValue}
            placeholder={rangePlaceholder}
            className="date-range-filter__input"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Box className="date-range-filter__adornment">
                  {displayValue ? (
                    <IconButton
                      className="date-range-filter__clear"
                      size="small"
                      onClick={handleClear}
                      aria-label="clear"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  ) : null}
                  {open ? (
                    <ChevronUp
                      size={18}
                      color="var(--color-foreground-subtle)"
                      aria-hidden="true"
                    />
                  ) : (
                    <ChevronDown
                      size={18}
                      color="var(--color-foreground-subtle)"
                      aria-hidden="true"
                    />
                  )}
                </Box>
              ),
            }}
          />
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "date-range-filter__paper" }}
      >
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={adapterLocale}
        >
          <Box className="date-range-filter__dual">
            <Box className="date-range-filter__header">
              <IconButton
                className="date-range-filter__nav"
                size="small"
                aria-label="previous month"
                onClick={() =>
                  setVisibleMonth((month) => month.subtract(1, "month"))
                }
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </IconButton>
              <Box className="date-range-filter__month-labels">
                <span className="date-range-filter__month-label">
                  {leftMonthLabel}
                </span>
                <span className="date-range-filter__month-label date-range-filter__month-label--secondary">
                  {rightMonthLabel}
                </span>
              </Box>
              <IconButton
                className="date-range-filter__nav"
                size="small"
                aria-label="next month"
                onClick={() =>
                  setVisibleMonth((month) => month.add(1, "month"))
                }
              >
                <ChevronRight size={18} aria-hidden="true" />
              </IconButton>
            </Box>
            <Box className="date-range-filter__calendars">
              {renderMonthCalendar(leftMonth)}
              <Box className="date-range-filter__calendar-secondary">
                {renderMonthCalendar(rightMonth)}
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

DateRangeFilterInput.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }),
};

DateRangeFilterInput.defaultProps = {
  name: "dateRange",
  value: null,
  labels: {
    from: "From Date",
    to: "To Date",
  },
};

export default DateRangeFilterInput;
